import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

interface RoutePreloadData {
    preload?: boolean;
    preloadOnSlowConnection?: boolean;
    priority?: number;
    delay?: number;
}

interface NetworkConnection {
    effectiveType?: string;
    saveData?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class SelectivePreloadingStrategy implements PreloadingStrategy {
    preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
        const data = route.data as RoutePreloadData | undefined;

        // Angular's RouterPreloader invokes this strategy for both `loadChildren`
        // and `loadComponent` lazy routes (the `load` callback resolves either),
        // so gate purely on the `preload` flag — not on `loadChildren`.
        if ((!route.loadChildren && !route.loadComponent) || !data?.preload) {
            return of(null);
        }

        if (this.shouldSkipPreload(data)) {
            return of(null);
        }

        const delay = data.delay ?? this.getDelayFromPriority(data.priority);

        return timer(delay).pipe(
            switchMap(() => load()),
            catchError(() => of(null))
        );
    }

    private shouldSkipPreload(data: RoutePreloadData): boolean {
        if (data.preloadOnSlowConnection) {
            return false;
        }

        const connection = this.getConnection();

        if (!connection) {
            return false;
        }

        return Boolean(connection.saveData) || ['slow-2g', '2g'].includes(connection.effectiveType ?? '');
    }

    private getDelayFromPriority(priority = 0): number {
        return Math.max(priority, 0) * 1500;
    }

    private getConnection(): NetworkConnection | null {
        const nav = navigator as Navigator & {
            connection?: NetworkConnection;
            mozConnection?: NetworkConnection;
            webkitConnection?: NetworkConnection;
        };

        return nav.connection ?? nav.mozConnection ?? nav.webkitConnection ?? null;
    }
}
